import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { AccountType } from '@constants/generic';
import UserPermissionTypes from '@constants/permissions';
import { STORAGE_KEYS } from '@constants/storage';
import { useLazyGetAllBranchesQuery } from '@features/branch-management/queries';
import useCheckPermission from '@hooks/checkPermission';
import { Branch } from '@types';
import { getLocalStorageItem, setLocalStorageItem } from '@utils/storage';

import BranchSwitcher from './BranchSwitcher';

interface BranchSelectionProps {
  onBranchChange: (branchId: string, branchName: string) => void;
  initialBranchName: string;
  handleAddNewBranch: (branchCount: number) => void;
}

const BranchSelection = ({
  onBranchChange,
  initialBranchName,
  handleAddNewBranch
}: BranchSelectionProps) => {
  const { currentOrg } = useSelector((state: any) => state.user);
  const { currentBranchFilter } = useSelector((state: any) => state.branch);

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const branchName = initialBranchName || currentOrg?.default_branch_name;

  const [branchesList, setBranchesList] = useState<Branch[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>(branchName);
  const { checkPermission } = useCheckPermission();

  const hasCreateBranchAccess = checkPermission([
    UserPermissionTypes.BRANCH_CREATE
  ]);

  const [
    getAllBranches,
    { isLoading: isLoadingBranches, isFetching: isFetchingBranches }
  ] = useLazyGetAllBranchesQuery();

  const handleGetAllBranches = async () => {
    const res = await getAllBranches({
      organizationId
    });
    if (res?.data) {
      setBranchesList(res.data?.data);
      if (res.data.data.length === 1) {
        setSelectedBranch(res.data.data[0].name);
        setLocalStorageItem(STORAGE_KEYS.BRANCH_NAME, res.data.data[0].name);
      }
    }
  };

  const handleBranchChange = (branch: string) => {
    setSelectedBranch(branch);
    const selectedBranchId = branchesList?.find(
      (b: Branch) => b.name === branch
    )?.id;
    if (selectedBranchId) onBranchChange(selectedBranchId, branch);
  };

  useEffect(() => {
    if (
      currentOrg?.id === organizationId &&
      currentOrg?.type === AccountType.ORGANIZATION
    ) {
      handleGetAllBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    organizationId,
    currentOrg?.id,
    currentBranchFilter?.branchName,
    currentBranchFilter?.imageUrl
  ]);

  useEffect(() => {
    if (branchName) setSelectedBranch(branchName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchName]);

  return (
    <BranchSwitcher
      selectedBranch={selectedBranch}
      branchesList={branchesList}
      handleAddNewBranch={handleAddNewBranch}
      hasCreateBranchAccess={hasCreateBranchAccess}
      onBranchChange={handleBranchChange}
      isLoading={isLoadingBranches || isFetchingBranches}
    />
  );
};

export default BranchSelection;
